import React from 'react';
import 'twin.macro';
import { useUser } from '@teetsh/app/src/containers/App/context/useUser';
import useUpdateUser from '@teetsh/app/src/queries/user/useUpdateUser';
import useClassrooms from '@teetsh/app/src/containers/App/context/ClassroomsContext/useClassrooms';
import { useParams } from 'react-router-dom';
import { CahierJournalView } from '../types';
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from '@teetsh/app/src/components/Resizable';
import { Sidebar } from './Sidebar';
import { Body } from './Body';
import getDateFromCJUrl from 'utils/time/getDateFromCJUrl';
import { UserLocale } from 'types/user';
import AutoSubmitForm from 'components/AutoSave';
import { Form } from 'react-final-form';
import { CJPDFSettings } from 'types/cj';
import { Classroom } from 'types/classroom';
import { useUserAccount } from 'queries/user/account/useUserAccount';

interface Props {
  initialValue: CJPDFSettings;
  onUpdate: (value: CJPDFSettings) => void;
  lng: UserLocale;
  tz: string;
  classrooms: Classroom[];
  schoolyearId: string;
  schoolId: string;
  view: CahierJournalView | 'days';
  limitedBgSet: boolean;
  date: Date;
}

export function Preview(props: Props) {
  const {
    initialValue,
    onUpdate,
    tz,
    lng,
    classrooms,
    schoolyearId,
    schoolId,
    view,
    date,
    limitedBgSet,
  } = props;

  return (
    <Form<CJPDFSettings>
      initialValues={initialValue}
      onSubmit={async values => {
        await onUpdate(values);
        return {};
      }}
    >
      {({ handleSubmit }) => (
        <form tw="min-h-0 h-full" onSubmit={handleSubmit}>
          <AutoSubmitForm />
          <div tw="w-full h-full flex flex-col overflow-hidden">
            <ResizablePanelGroup
              direction="horizontal"
              autoSaveId="cahier-journal-sidebar"
            >
              <Sidebar
                view={view === 'days' ? 'week' : view}
                limitedBgSet={limitedBgSet}
                date={date}
                classrooms={classrooms}
                lng={lng}
                tz={tz}
              />
              <ResizableHandle />
              <ResizablePanel order={1}>
                <div tw="h-full overflow-auto">
                  <Body
                    schoolyearId={schoolyearId}
                    schoolId={schoolId}
                    view={view}
                    date={date}
                    lng={lng}
                    tz={tz}
                  />
                </div>
              </ResizablePanel>
            </ResizablePanelGroup>
          </div>
        </form>
      )}
    </Form>
  );
}

export function AuthUserPreview() {
  const user = useUser();
  const updateUser = useUpdateUser();
  const classrooms = useClassrooms();
  const { data: userAccount } = useUserAccount();

  const params = useParams<{
    schoolyearId: string;
    schoolId: string;
    view: CahierJournalView | 'days';
    year: string;
    month: string;
    day: string;
  }>();
  const view = params.view as CahierJournalView | 'days';
  const date = getDateFromCJUrl(
    params.year as string,
    params.month as string,
    params.day as string,
  );

  return (
    <Preview
      classrooms={classrooms}
      initialValue={user.fonts.cj}
      onUpdate={values =>
        updateUser({
          ...user,
          fonts: {
            ...user.fonts,
            cj: values,
          },
        })
      }
      lng={user.locale}
      tz={user.location}
      view={view}
      date={date}
      schoolyearId={params.schoolyearId as string}
      schoolId={params.schoolId as string}
      limitedBgSet={userAccount?.isOnOldPricing || false}
    />
  );
}
